import type { RefObject } from 'react';
import { useEffect, useState, useTransition } from 'react';
import useDetectKeyboardOpen from 'use-detect-keyboard-open';

import { useBodyOverlay } from '../../../hooks/body-overlay';
import { useKeyboardClose } from '../../../hooks/keyboard';
import { useOnClickOutside } from '../../../hooks/mouse';
import type { DispatchState } from '../../../types/React';
import type { SearchParams } from '../../../types/search';
import { useInput } from './input';
import { getBackButtonClickHandler } from './input/handlers';
import { useRecentSearches } from './recent-searches';
import { useSuggestions } from './suggestions';
import type { SuggestionsConfig } from './suggestions/types';

interface UseSearchOptions {
  matchesTablet: boolean;
  config: SuggestionsConfig;
  params: SearchParams;
  withRecentSearches: boolean;
  withProductSuggestions: boolean;
  shopName?: string;
  filterQuery?: string;
  overlayStacking?: number;
  withInputFilled: boolean;
  parentRef?: RefObject<HTMLElement>;
}

interface UseBodyOverlayEffectOptions extends Pick<UseSearchOptions, 'matchesTablet'> {
  focused: boolean;
  isSuggestionsOpened: boolean;
  setIsBodyOverlayVisible: DispatchState<boolean>;
}

const useBodyOverlayEffect = ({
  matchesTablet,
  focused,
  isSuggestionsOpened,
  setIsBodyOverlayVisible,
}: UseBodyOverlayEffectOptions) => {
  useEffect(() => {
    setIsBodyOverlayVisible(matchesTablet && (focused || isSuggestionsOpened));
  }, [focused, isSuggestionsOpened]);
};

const getSearchSuggestionOptions = ({
  suggestions,
  isOpen,
  suggestionsHandlers,
  recentSearches,
  handleSelect,
}) => {
  return {
    suggestions: suggestions.suggestions,
    productSuggestions: suggestions.productSuggestions,
    listItems: suggestions.listItems,
    flags: { isOpen },
    handlers: {
      ...suggestionsHandlers,
      ...(recentSearches?.flags.isOpen
        ? { handleSelect: recentSearches.handlers.handleSelect }
        : { handleSelect }),
    },
  };
};

const useCloseSuggestions = (
  setIsBodyOverlayVisible: (visible: boolean) => void,
  setIsContainerHidden: (hidden: boolean) => void
) => {
  const [, startTransition] = useTransition();

  return () => {
    startTransition(() => {
      setIsBodyOverlayVisible(false);
      setIsContainerHidden(true);
    });
  };
};

export const useSearch = (props: UseSearchOptions) => {
  const { config, params, withProductSuggestions, filterQuery, withRecentSearches, ...inputProps } =
    props;
  const { matchesTablet, shopName, withInputFilled, overlayStacking, parentRef } = inputProps;

  const isMobile = !matchesTablet;
  const isKeyboardOpen = useDetectKeyboardOpen();
  const { isBodyOverlayVisible, setIsBodyOverlayVisible } = useBodyOverlay(
    overlayStacking,
    parentRef
  );
  const [isContainerHidden, setIsContainerHidden] = useState(false);
  const closeSuggestions = useCloseSuggestions(setIsBodyOverlayVisible, setIsContainerHidden);

  const input = useInput({ isMobile, shopName, withInputFilled, onBlur: closeSuggestions });
  const { clearValue, unlockSearchAndClearInput, ...inputHandlers } = input.handlers;
  const isInputFocused = input.flags.focused;
  const suggestions = useSuggestions({
    config,
    params,
    inputValue: input.value,
    setIsBodyOverlayVisible,
    isInputFocused,
    isEditedByUser: input.flags.isEditedByUser,
    isMobile,
    unlockSearchAndClearInput: input.handlers.unlockSearchAndClearInput,
    isContainerHidden,
    setIsContainerHidden,
    withProductSuggestions,
    urlParams: filterQuery,
  });
  const { clear, handleSelect, ...suggestionsHandlers } = suggestions.handlers;

  const recentSearches = useRecentSearches({
    isEnabled: withRecentSearches,
    isSuggestionsOpen: suggestions.flags.isOpen,
    isInputFocused,
    isContainerHidden,
    isInputLocked: input.flags.isLocked,
    isMobile,
    inputValue: input.value,
    inputRef: input.refs.input,
  });
  const isOpen = suggestions.flags.isOpen || !!recentSearches?.flags.isOpen;

  const handleBackButtonClick = getBackButtonClickHandler({
    unlockSearchAndClearInput,
    clearSuggestions: clear,
  });

  useBodyOverlayEffect({
    matchesTablet,
    focused: isInputFocused,
    isSuggestionsOpened: isOpen,
    setIsBodyOverlayVisible,
  });

  useEffect(() => {
    if (isInputFocused) {
      setIsContainerHidden(false);
    }
  }, [isInputFocused]);

  useOnClickOutside(() => {
    if (isOpen) {
      closeSuggestions();
    }
  }, input.refs.container);

  useKeyboardClose(isOpen || input.flags.focused, () => {
    input.handlers.clearValue();
    if (input.flags.isLocked) {
      input.handlers.unlockSearch();
    }
    if (document.activeElement !== input.refs.input.current) {
      closeSuggestions();
    }
  });

  return {
    input: {
      value: input.value,
      actionPath: input.actionPath,
      refs: input.refs,
      placeholder: input.placeholder,
      flags: {
        focused: isInputFocused,
        isLocked: input.flags.isLocked,
        isClearButtonHidden: input.flags.isClearButtonHidden,
        isKeyboardOpen,
        isBodyOverlayVisible,
      },
      handlers: { ...inputHandlers, handleBackButtonClick },
    },
    suggestions: getSearchSuggestionOptions({
      suggestions,
      isOpen,
      suggestionsHandlers,
      recentSearches,
      handleSelect,
    }),
    recentSearches,
  };
};
